import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Container, Spinner, Alert } from 'react-bootstrap';
import api from '../api/api';
import '../styles/RecruiterJoinPage.css';

const RecruiterJoinPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [retryCount, setRetryCount] = useState(0);
  const [loadingStatus, setLoadingStatus] = useState('Verifying your invitation...');

  // Check for redirect after reload
  useEffect(() => {
    const redirectPath = sessionStorage.getItem('redirectAfterReload');
    if (redirectPath) {
      sessionStorage.removeItem('redirectAfterReload');
      window.location.href = redirectPath;
      return;
    }
  }, []);

  // Check if already authenticated as a recruiter
  useEffect(() => {
    const checkExistingAuth = () => {
      const storedToken = localStorage.getItem('authToken');
      const storedUserType = localStorage.getItem('userType');
      const storedUser = localStorage.getItem('userData');
      
      if (storedToken && storedUserType === 'recruiter' && storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          if (userData.email === email) {
            window.location.href = '/recruiter/dashboard';
            return true;
          }
        } catch (e) {
          console.error('Error parsing stored user data:', e);
        }
      }
      return false;
    };
    
    if (!checkExistingAuth()) {
      autoLogin();
    }
  }, [token, email, navigate]);

  // Auto-login with token
  const autoLogin = async () => {
    if (!token || !email) {
      setError('Invalid invitation link. Please contact your employer for a new invitation.');
      setLoading(false);
      return;
    }

    try {
      setLoadingStatus('Verifying your invitation...');
      
      // Fix the API endpoint path - remove the /api prefix to avoid double prefix issues
      const response = await api.post('/recruiters/verify-invitation', { token, email });
      
      if (response.data && response.data.success) {
        
        try {
          setLoadingStatus('Setting up your account...');
          
          // Extract data from the wrapped response
          const responseData = response.data.data || response.data;
          const token = responseData.token;
          const recruiter = responseData.recruiter;
          
          if (!token) {
            throw new Error('Token not found in API response');
          }
          
          // Store auth data in localStorage
          localStorage.setItem('authToken', token);
          localStorage.setItem('userType', 'recruiter');
          
          // Ensure we have all required user data
          const userData = {
            id: recruiter?.id || 'unknown',
            email: recruiter?.email || email,
            name: recruiter?.name || 'Recruiter',
            userType: 'recruiter',
            profileCompleted: true
          };
          
          localStorage.setItem('userData', JSON.stringify(userData));
          
          // Force page reload and redirect
          setLoadingStatus('Redirecting to dashboard...');
          
          // Use a short timeout to ensure state updates are processed before redirect
          setTimeout(() => {
            // Verify that the token was properly set
            const verifyToken = localStorage.getItem('authToken');
            const verifyUserData = localStorage.getItem('userData');
            
            if (verifyToken && verifyUserData) {
              // First reload the page to ensure AuthContext initializes with the new token
              // Then redirect to the dashboard
              sessionStorage.setItem('redirectAfterReload', '/recruiter/dashboard');
              window.location.reload();
            } else {
              console.error('Authentication data not properly set. Please try again.');
              setError('Authentication failed. Please try again or contact support.');
              setLoading(false);
            }
          }, 1000);
        } catch (storageError) {
          console.error('Error storing auth data:', storageError);
          console.error('Storage error details:', {
            name: storageError?.name,
            message: storageError?.message,
            stack: storageError?.stack
          });
          setError('Failed to store authentication data. Please try again.');
          setLoading(false);
        }
      } else {
        console.error('API returned error:', response.data);
        if (response.data?.message?.includes('revoked')) {
          setError('Your access has been revoked by your employer. Please contact them if you believe this is an error.');
        } else {
          setError(response.data?.message || 'This invitation link is invalid or has expired.');
        }
        setLoading(false);
      }
    } catch (apiError) {
      console.error('API request failed:', apiError);
      console.error('Error details:', {
        message: apiError.message,
        status: apiError.response?.status,
        statusText: apiError.response?.statusText,
        responseData: apiError.response?.data,
        config: {
          url: apiError.config?.url,
          method: apiError.config?.method,
          baseURL: apiError.config?.baseURL,
          headers: apiError.config?.headers
        }
      });
      
      // Implement retry with exponential backoff
      if (retryCount < 3) {
        setRetryCount(retryCount + 1);
        setTimeout(() => autoLogin(), 1000 * Math.pow(2, retryCount));
        return;
      }
      
      if (apiError.response?.status === 403) {
        setError('Your access has been revoked by your employer. Please contact them if you believe this is an error.');
      } else if (apiError.response?.status === 401) {
        setError(`Invalid invitation token. Please check your invitation link or contact your employer for a new one. Details: ${apiError.response?.data?.message || 'Unknown error'}`);
      } else if (apiError.response?.status === 404) {
        setError(`Recruiter not found. Please contact your employer to ensure you've been properly invited. Email: ${email}`);
      } else {
        setError(`Failed to verify invitation. Error: ${apiError.message}. Please try again later.`);
      }
      setLoading(false);
    }
  };

  // Show loading spinner while attempting auto-login
  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">{loadingStatus}</p>
          <div className="progress mt-3" style={{ height: '5px' }}>
            <div 
              className="progress-bar progress-bar-striped progress-bar-animated" 
              role="progressbar" 
              style={{ width: '100%' }} 
              aria-valuenow="100" 
              aria-valuemin="0" 
              aria-valuemax="100"
            ></div>
          </div>
        </div>
      </Container>
    );
  }

  // Show error if auto-login fails
  return (
    <Container className="recruiter-join-container">
      <div className="text-center">
        <img src="/images/logo.png" alt="GenZ Portal Logo" className="mb-3" style={{ maxHeight: '60px' }} />
        <h2>GenZ Portal Access</h2>
        {error && <Alert variant="danger" className="mt-4">{error}</Alert>}
        
        <div className="mt-4">
          <button 
            onClick={() => {
              setError(null);
              setLoading(true);
              setRetryCount(0);
              autoLogin();
            }}
            className="btn btn-primary me-3"
          >
            Try Again
          </button>
          
          <a 
            href={`mailto:support@genzportal.com?subject=Recruiter%20Invitation%20Issue&body=I'm%20having%20trouble%20accessing%20my%20recruiter%20account%20with%20email:%20${email}`}
            className="btn btn-outline-secondary"
          >
            Contact Support
          </a>
        </div>
        
        <p className="mt-4">
          If you continue to experience issues, please contact your employer for assistance.
        </p>
      </div>
    </Container>
  );
};

export default RecruiterJoinPage;
